import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * 最下部判定の許容誤差（px）
 *
 * 実機（DPRが端数の端末。例: Pixel 8 = 2.625）では window.scrollY が小数値を返し、
 * 最下部までスクロールしても innerHeight + scrollY が scrollHeight にわずかに届かない。
 * 厳密な等値比較だと永遠に最下部とみなされないため、数pxの許容誤差で吸収する。
 */
const BOTTOM_THRESHOLD_PX = 2;

/**
 * useScrollListener オプション
 */
interface UseScrollListenerOptions {
  onReachBottom?: () => void;
}

/**
 * useScrollListener フックの戻り値
 */
interface UseScrollListenerReturn {
  hasReachedBottom: boolean;
}

/**
 * スクロール検知を行うフック
 * 最下部までスクロールしたかどうかを検知する
 */
export const useScrollListener = (options?: UseScrollListenerOptions): UseScrollListenerReturn => {
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const hasCalledCallback = useRef(false);

  const handleScroll = useCallback(() => {
    const isBottom =
      window.innerHeight + window.scrollY >=
      document.documentElement.scrollHeight - BOTTOM_THRESHOLD_PX;

    if (isBottom) {
      setHasReachedBottom(true);

      // コールバックは1回のみ呼び出す
      if (options?.onReachBottom && !hasCalledCallback.current) {
        hasCalledCallback.current = true;
        options.onReachBottom();
      }
    }
  }, [options]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return { hasReachedBottom };
};
